/**
 * Snackbar reporting a finished add, so the user can carry on adding the same
 * organization to more events. Follows the export-history snackbar: a fixed,
 * dismissible positive Message that hides itself after a while.
 */

import React, { useEffect } from 'react';
import { Message } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { SNACKBAR_DURATION_MS, i18nKeys } from '../constants';
import { AddSummary } from '../types';
import styles from '../AddOrganizationToEvents.module.css';

interface AddSummaryPanelProps {
  organizationName: string;
  summary: AddSummary;
  onDismiss: () => void;
}

const AddSummaryPanel: React.FC<AddSummaryPanelProps> = ({
  organizationName,
  summary,
  onDismiss
}) => {
  // Restart the timer for each new summary object, so a second add is shown in full.
  useEffect(() => {
    const timer = setTimeout(onDismiss, SNACKBAR_DURATION_MS);
    return () => clearTimeout(timer);
  }, [summary, onDismiss]);

  return (
    <div className={styles.snackbar} role="status">
      <Message positive onDismiss={onDismiss}>
        <Message.Header>
          <Translate value={i18nKeys.DONE.TITLE} />
        </Message.Header>
        <p>
          {I18n.t(i18nKeys.DONE.ADDED, {
            name: organizationName,
            count: summary.added
          })}
        </p>
        {summary.unchanged > 0 && (
          <p>{I18n.t(i18nKeys.DONE.UNCHANGED, { count: summary.unchanged })}</p>
        )}
        {summary.skipped > 0 && (
          <p>{I18n.t(i18nKeys.DONE.SKIPPED, { count: summary.skipped })}</p>
        )}
      </Message>
    </div>
  );
};

export default AddSummaryPanel;
